import styled from '@emotion/styled';
import CollectionsIcon from '@mui/icons-material/AccountBalance';
import AppsIcon from '@mui/icons-material/Apps';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BackspaceIcon from '@mui/icons-material/Backspace';
import BuildIcon from '@mui/icons-material/Build';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import MoneyIcon from '@mui/icons-material/CurrencyExchange';
import Dashboard from '@mui/icons-material/Dashboard';
import DifferenceIcon from '@mui/icons-material/Difference';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmailIcon from '@mui/icons-material/Email';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import IcecreamIcon from '@mui/icons-material/Icecream';
import LocalConvenienceStoreIcon from '@mui/icons-material/LocalConvenienceStore';
import LunchDiningIcon from '@mui/icons-material/LunchDining';
import EntriesIcon from '@mui/icons-material/NoteAlt';
import PaidIcon from '@mui/icons-material/Paid';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ReplayIcon from '@mui/icons-material/Replay';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import RuleIcon from '@mui/icons-material/Rule';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import SummarizeIcon from '@mui/icons-material/Summarize';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import TextField from '@mui/material/TextField';
import { Box, Tooltip, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { NestedList } from '../../components/NestedList';
import routes from '../../config/routes';
import { hasPermissions } from '../../helpers';
import { userPermissionsDataSelector } from '../../state';
import ListExternalLinkButton from './ListExternalLinkButton';
import useParametersLoading from '../../hooks/useParametersLoading';

const FAVORITES_KEY = 'sidebar-favorite-routes';

const othersRoutesNav = [
  {
    route: routes.protectedNestedRoutes.dashboard.index,
    icon: <Dashboard />,
    tranlationKey: 'dashboard.title',
  },
  {
    route: routes.protectedRoutes.analytics,
    icon: <QueryStatsIcon />,
    tranlationKey: 'analytics.menu',
  },
];

const reportsRoutesNav = [
  {
    route: routes.protectedNestedRoutes.accountingEntries.index,
    icon: <EntriesIcon />,
    tranlationKey: 'accountingEntries.button',
    noLink: true,
    children: [
      {
        route: routes.protectedNestedRoutes.accountingEntries.index,
        icon: <MoneyIcon />,
        tranlationKey: 'accountingEntries.title',
      },
      {
        route: routes.protectedNestedRoutes.companyCodes.index,
        icon: <LocalConvenienceStoreIcon />,
        tranlationKey: 'companyCodes.titles.menu',
      },
      {
        route: routes.protectedNestedRoutes.accountingParameters.index,
        icon: <BuildIcon />,
        tranlationKey: 'accountingParameters.title',
      },
      {
        route: routes.protectedNestedRoutes.monthClosing.index,
        icon: <EventAvailableIcon />,
        tranlationKey: 'monthClosing.title',
      },
    ],
  },
  {
    route: routes.protectedRoutes.help,
    icon: <PaidIcon />,
    tranlationKey: 'sale-totals.button',
    noLink: true,
    children: [
      {
        route: routes.protectedRoutes.saleTotals,
        icon: <AttachMoneyIcon />,
        tranlationKey: 'sale-totals.title',
      },
      { route: routes.protectedRoutes.gcs, icon: <ReceiptIcon />, tranlationKey: 'gcs.title' },
      {
        route: routes.protectedRoutes.gcsByProduct,
        icon: <LunchDiningIcon />,
        tranlationKey: 'gcs-by-product.title',
      },
      {
        route: routes.protectedRoutes.salesByProduct,
        icon: <FastfoodIcon />,
        tranlationKey: 'sale-by-product.title',
      },
      {
        route: routes.protectedRoutes.reportTender,
        icon: <RequestPageIcon />,
        tranlationKey: 'report-tender.title',
      },
      {
        route: routes.protectedRoutes.thirdPartySalesByItems,
        icon: <IcecreamIcon />,
        tranlationKey: 'thirdPartySalesByItems.title',
      },
      {
        route: routes.protectedRoutes.cancellations,
        icon: <BackspaceIcon />,
        tranlationKey: 'cancellations.title',
      },
      {
        route: routes.protectedRoutes.oakBrook,
        icon: <SimCardDownloadIcon />,
        tranlationKey: 'oak-brook.title',
      },
    ],
  },
  {
    route: routes.protectedRoutes.profile,
    icon: <CollectionsIcon />,
    tranlationKey: 'toolbar.labels.collections',
    noLink: true,
    children: [
      {
        route: routes.protectedRoutes.reliefs,
        icon: <PointOfSaleIcon />,
        tranlationKey: 'reliefs.title',
      },
      {
        route: routes.protectedRoutes.pettyCashDifference,
        icon: <DifferenceIcon />,
        tranlationKey: 'petty-cash-difference.title',
      },
      {
        route: routes.protectedRoutes.EconomicAccountingReport,
        icon: <LocalAtmIcon />,
        tranlationKey: 'economic-accounting-report.title',
      },
      {
        route: routes.protectedRoutes.CashMovementReport,
        icon: <TrendingUpIcon />,
        tranlationKey: 'cash-movement-report.title',
      },
      {
        route: routes.protectedRoutes.pettyCashWithDrawalReport,
        icon: <InsertChartIcon />,
        tranlationKey: 'accounting-info-report.title',
      },
      {
        route: routes.protectedRoutes.EnvelopeDetailsReport,
        icon: <EmailIcon />,
        tranlationKey: 'envelope-details-report.title',
      },
    ],
  },
  {
    route: routes.protectedNestedRoutes.missingClosingDetail.index,
    icon: <RuleIcon />,
    tranlationKey: 'toolbar.labels.incorporation',
    noLink: true,
    children: [
      {
        route: routes.protectedNestedRoutes.missingClosingDetail.index,
        icon: <SummarizeIcon />,
        tranlationKey: 'missing-closing-detail.title',
      },
      {
        route: routes.protectedNestedRoutes.reprocess.index,
        icon: <ReplayIcon />,
        tranlationKey: 'reprocess.title',
      },
      {
        route: routes.protectedRoutes.mastersImport,
        icon: <CloudSyncIcon />,
        tranlationKey: 'masters-import.title',
      },
      {
        route: routes.protectedRoutes.interfaces,
        icon: <DeviceHubIcon />,
        tranlationKey: 'interfaces.titles.principal',
      },
      // {
      //   route: routes.protectedNestedRoutes.pendingInterfaces.index,
      //   icon: <SummarizeIcon />,
      //   tranlationKey: 'pending-interfaces.titles.principal',
      //   noLink: true,
      //   children: [
      //     {
      //       route: routes.protectedRoutes.pendingInterfaces,
      //       icon: <SummarizeIcon />,
      //       tranlationKey: 'pending-interfaces.titles.interfaces_state',
      //     },
      //     {
      //       route: routes.protectedNestedRoutes.pendingInterfaces.index,
      //       icon: <SummarizeIcon />,
      //       tranlationKey: 'pending-interfaces.titles.execute_interfaces',
      //     }
      //   ]
      // },
    ],
  },
  // {
  //   route: routes.protectedRoutes.salesJournal,
  //   icon: <BookIcon />,
  //   tranlationKey: 'sales-journal.title',
  // },
];

const LastList = styled(List)`
  flex-grow: 1;
`;

const SearchContainer = styled(Box)`
  padding: ${({ theme }) => theme.spacing(0.5, 1.25, 1)};
`;

const FavoriteRow = styled(Box)`
  display: flex;
  align-items: center;
  width: 100%;
`;

interface SideBarNavProps {
  isSidebarOpen: boolean;
}

const SidebarNav = ({ isSidebarOpen }: SideBarNavProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [favorites, setFavorites] = useState<string[]>(() =>
    JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]')
  );
  const permissions = useSelector(userPermissionsDataSelector).map(i => i.permission);
  const { loaded } = useParametersLoading();
  const dashboardRoutesAccess = useMemo(
    () => othersRoutesNav.filter(i => hasPermissions(permissions, i.route.permissions)),
    [permissions]
  );
  const reportsRoutesAccess = useMemo(
    () =>
      reportsRoutesNav
        .map(route => ({
          ...route,
          children: route.children.filter(child =>
            hasPermissions(permissions, child.route.permissions)
          ),
        }))
        .filter(route => route.children.length > 0),
    [permissions]
  );

  const leafRoutesAccess = useMemo(
    () => [...dashboardRoutesAccess, ...reportsRoutesAccess.flatMap(route => route.children)],
    [dashboardRoutesAccess, reportsRoutesAccess]
  );

  const searchResults = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return [];
    return leafRoutesAccess.filter(route =>
      t(route.tranlationKey).toString().toLowerCase().includes(normalizedSearch)
    );
  }, [leafRoutesAccess, search, t]);

  const favoriteRoutes = useMemo(
    () => leafRoutesAccess.filter(route => favorites.includes(route.route.path)),
    [favorites, leafRoutesAccess]
  );

  const toggleFavorite = (path: string) => {
    setFavorites(current => {
      const next = current.includes(path)
        ? current.filter(item => item !== path)
        : [...current, path];
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
      return next;
    });
  };

  const renderSearchItem = (item: (typeof leafRoutesAccess)[number]) => {
    const isFavorite = favorites.includes(item.route.path);
    return {
      ...item,
      tranlationKey: item.tranlationKey,
      icon: (
        <FavoriteRow>
          {item.icon}
          {isSidebarOpen && (
            <Tooltip
              title={
                isFavorite
                  ? t('layout.drawer.search.removeFavorite')
                  : t('layout.drawer.search.addFavorite')
              }
            >
              <IconButton
                size="small"
                edge="end"
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  toggleFavorite(item.route.path);
                }}
              >
                {isFavorite ? (
                  <StarIcon color="secondary" fontSize="small" />
                ) : (
                  <StarBorderIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </FavoriteRow>
      ),
    };
  };

  const renderNavigationContent = () => {
    if (search && searchResults.length > 0) {
      return (
        <NestedList
          data={searchResults.map(renderSearchItem)}
          subheader={t('layout.drawer.search.results')}
        />
      );
    }

    if (search) {
      return (
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1 }}>
          {t('layout.drawer.search.empty')}
        </Typography>
      );
    }

    return (
      <>
        {favoriteRoutes.length > 0 && (
          <>
            <NestedList
              data={favoriteRoutes.map(renderSearchItem)}
              subheader={t('layout.drawer.search.favorites')}
            />
            <Divider />
          </>
        )}
        <NestedList data={dashboardRoutesAccess} />
      </>
    );
  };

  return (
    <>
      {isSidebarOpen && (
        <SearchContainer data-tour="sidebar-search">
          <TextField
            fullWidth
            size="small"
            value={search}
            placeholder={t('layout.drawer.search.placeholder')}
            onChange={event => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </SearchContainer>
      )}
      {renderNavigationContent()}
      <Divider />
      {!search && loaded && (
        <NestedList data={reportsRoutesAccess} subheader={t('layout.drawer.reports')} />
      )}
      <Divider />
      <LastList>
        <ListExternalLinkButton
          icon={<AppsIcon />}
          to={process.env.FLEX_TILES_URL || '/'}
          translationKey="layout.drawer.flexTiles"
          showTooltip={!isSidebarOpen}
        />
      </LastList>
    </>
  );
};

export default SidebarNav;
